import { ProfileQueryKeys } from '../types/query-keys'
import { getProfile } from '../services/profile'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useProfileStore } from '../store/profile'
import { useEffect, useMemo } from 'react'
import { validateConnectionType } from '../utilities/profile'
import { useUser } from 'containers/user/hooks/useUser'
import { User } from 'containers/user/types/user'
import { IUser } from '@/interfaces/user'
import { ClientMethodsTypes, client } from '@/services/v2/client'
import { environment } from '@/shared/consts/environment'
import { SubscriptionProductNames } from '@/interfaces/subscription'
import { ProfileRecommendation } from 'containers/recommendations/types/recommendations'
import { isModUserId } from '@/shared/utils'

const BASE_URL = '/api/v1/users'

export const useProfile = () => {
  const { setProfile, profile } = useProfileStore()
  const { isLoading: loadingUser } = useUser()
  const {
    query: { username },
  } = useRouter()

  const { data, isLoading, ...rest } = useQuery({
    queryKey: [ProfileQueryKeys.PROFILE, String(username)?.toLocaleLowerCase()],
    queryFn: () => getProfile(String(username)),
    enabled: !!username,
    onSuccess: (response) => {
      setProfile(response)
    },
  })

  useEffect(() => {
    setProfile(data)
  }, [data, username, setProfile])

  return {
    profile,
    isLoading:
      isLoading ||
      loadingUser ||
      (username !== profile?.username?.toLocaleLowerCase() && rest.isFetching),
    ...rest,
  }
}

export const useProfileByUsername = (username: string) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: [ProfileQueryKeys.PROFILE, username],
    queryFn: () => getProfile(username),
    enabled: !!username,
  })

  return {
    profile: data,
    isLoading,
    ...rest,
  }
}

export const useProfileById = (id: string) => {
  const { data, isLoading, ...rest } = useQuery({
    queryKey: [ProfileQueryKeys.PROFILE, id],
    queryFn: () =>
      client<User[]>(`${BASE_URL}/retrieve-users`, {
        method: ClientMethodsTypes.POST,
        body: [id],
      }),
    enabled: !!id,
  })

  return {
    profile: data?.[0],
    isLoading,
    ...rest,
  }
}

export const useProfilesByIds = (id: string[]) => {
  return useQuery({
    queryKey: [ProfileQueryKeys.PROFILE, id],
    queryFn: () =>
      client<User[]>(`${BASE_URL}/retrieve-users`, {
        method: ClientMethodsTypes.POST,
        body: id,
      }),
    enabled: !!id?.length,
  })
}

export const useHiddenProfileById = (id: string) => {
  return useQuery({
    queryKey: [ProfileQueryKeys.PROFILE, id, ProfileQueryKeys.HIDDEN_PROFILE],
    queryFn: () => client<User>(`${BASE_URL}/retrieve-anonymous-user/${id}`),
    enabled: !!id,
  })
}

export const useIsOwnProfile = (profile?: IUser | User) => {
  const { profile: currentProfile } = useProfileStore()
  const { user } = useUser()

  return useMemo(() => {
    const profileToCheck = profile || currentProfile
    return user?.id === profileToCheck?.id
  }, [profile, currentProfile, user?.id])
}

export const useProfileRelationType = (profile?: IUser | User) => {
  const { profile: currentProfile } = useProfileStore()
  const { user } = useUser()

  return useMemo(() => {
    return validateConnectionType({ user, profile: profile || currentProfile })
  }, [currentProfile, profile, user])
}

export const useIsMod = (profile?: IUser | User | ProfileRecommendation) => {
  const { user } = useUser()
  const modUser = profile || user

  return useMemo(() => {
    const IS_PROD = environment.env === 'production'

    return (
      isModUserId(modUser?.id) ||
      (!IS_PROD && modUser?.email?.includes('creatorland.com'))
    )
  }, [modUser?.email, modUser?.id])
}

export const useSubscription = (
  profile?: IUser | User | ProfileRecommendation
) => {
  const { user } = useUser()
  const subUser = profile || user

  return useMemo(() => {
    return {
      isSubscribed: subUser?.subscribed || false,
      subscriptions:
        subUser?.subscriptions ||
        ({} as Record<SubscriptionProductNames, boolean>),
    }
  }, [subUser?.subscriptions, subUser?.subscribed])
}
